import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, supportPlansApi, clientsApi } from '@/services/api';
import { downloadCSV } from '@/utils/csv';
import { HiOutlinePlus, HiOutlineDocumentText, HiOutlineDownload, HiOutlineEye, HiX } from 'react-icons/hi';

interface SupportPlan {
  plan: {
    id: string;
    planNumber: string;
    planType: string;
    startDate: string;
    endDate: string;
    clientWish: string;
    longTermGoal: string;
    shortTermGoal: string;
    supportContent: string;
    status: string;
    createdAt: string;
  };
  client: {
    id: string;
    clientNumber: string;
    lastName: string;
    firstName: string;
  } | null;
}

interface Client {
  id: string;
  lastName: string;
  firstName: string;
  clientNumber: string;
}

const planTypeLabels: Record<string, string> = {
  initial: '初回',
  regular: '定期',
  revision: '見直し',
};

const statusLabels: Record<string, { label: string; class: string }> = {
  draft: { label: '下書き', class: 'badge-gray' },
  pending: { label: '承認待ち', class: 'badge-warning' },
  approved: { label: '承認済', class: 'badge-success' },
  cancelled: { label: 'キャンセル', class: 'badge-danger' },
};

export default function SupportPlansPage() {
  const [isExporting, setIsExporting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    clientId: '',
    planType: 'initial',
    startDate: '',
    endDate: '',
    clientWish: '',
    longTermGoal: '',
    shortTermGoal: '',
    supportContent: '',
  });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{ data: SupportPlan[] }>({
    queryKey: ['support-plans'],
    queryFn: () => api.get('/api/v1/support-plans') as Promise<{ data: SupportPlan[] }>,
  });

  const { data: clientsData } = useQuery<{ data: Client[] }>({
    queryKey: ['clients-list'],
    queryFn: () => clientsApi.list({ limit: 100 }) as Promise<{ data: Client[] }>,
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => supportPlansApi.create(data),
    onSuccess: () => {
      toast.success('個別支援計画を作成しました');
      queryClient.invalidateQueries({ queryKey: ['support-plans'] });
      setShowCreateModal(false);
      setFormData({
        clientId: '',
        planType: 'initial',
        startDate: '',
        endDate: '',
        clientWish: '',
        longTermGoal: '',
        shortTermGoal: '',
        supportContent: '',
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || '作成に失敗しました');
    },
  });

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientId) {
      toast.error('利用者を選択してください');
      return;
    }
    if (!formData.startDate) {
      toast.error('開始日を入力してください');
      return;
    }
    createMutation.mutate(formData);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadCSV('/api/v1/export/support-plans');
      toast.success('CSVをダウンロードしました');
    } catch (error) {
      toast.error('ダウンロードに失敗しました');
    } finally {
      setIsExporting(false);
    }
  };

  const plans = data?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">個別支援計画</h1>
          <p className="text-gray-600 mt-1">利用者の個別支援計画を管理します</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleExport}
            disabled={isExporting}
            className="btn-secondary"
          >
            <HiOutlineDownload className="w-5 h-5 mr-2" />
            {isExporting ? 'エクスポート中...' : 'CSVエクスポート'}
          </button>
          <button onClick={() => setShowCreateModal(true)} className="btn-primary">
            <HiOutlinePlus className="w-5 h-5 mr-2" />
            新規作成
          </button>
        </div>
      </div>

      {/* 作成モーダル */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">個別支援計画 新規作成</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <HiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateSubmit} className="p-4 space-y-4">
              <div>
                <label className="label">利用者 *</label>
                <select
                  value={formData.clientId}
                  onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                  className="input w-full"
                  required
                >
                  <option value="">選択してください</option>
                  {clientsData?.data?.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.lastName} {client.firstName} ({client.clientNumber})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">計画種別</label>
                <select
                  value={formData.planType}
                  onChange={(e) => setFormData({ ...formData, planType: e.target.value })}
                  className="input w-full"
                >
                  <option value="initial">初回</option>
                  <option value="regular">定期</option>
                  <option value="revision">見直し</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">開始日 *</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="label">終了日</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="input w-full"
                  />
                </div>
              </div>
              <div>
                <label className="label">本人の希望</label>
                <textarea
                  value={formData.clientWish}
                  onChange={(e) => setFormData({ ...formData, clientWish: e.target.value })}
                  className="input w-full"
                  rows={2}
                />
              </div>
              <div>
                <label className="label">長期目標</label>
                <textarea
                  value={formData.longTermGoal}
                  onChange={(e) => setFormData({ ...formData, longTermGoal: e.target.value })}
                  className="input w-full"
                  rows={2}
                />
              </div>
              <div>
                <label className="label">短期目標</label>
                <textarea
                  value={formData.shortTermGoal}
                  onChange={(e) => setFormData({ ...formData, shortTermGoal: e.target.value })}
                  className="input w-full"
                  rows={2}
                />
              </div>
              <div>
                <label className="label">支援内容</label>
                <textarea
                  value={formData.supportContent}
                  onChange={(e) => setFormData({ ...formData, supportContent: e.target.value })}
                  className="input w-full"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary">
                  キャンセル
                </button>
                <button type="submit" disabled={createMutation.isPending} className="btn-primary">
                  {createMutation.isPending ? '作成中...' : '作成'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>計画番号</th>
                <th>利用者</th>
                <th>計画種別</th>
                <th>開始日</th>
                <th>終了日</th>
                <th>状態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500">
                    読み込み中...
                  </td>
                </tr>
              ) : plans.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12">
                    <HiOutlineDocumentText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">個別支援計画がありません</p>
                    <p className="text-sm text-gray-400 mt-2">「新規作成」ボタンから計画を作成してください</p>
                  </td>
                </tr>
              ) : (
                plans.map(({ plan, client }) => (
                  <tr key={plan.id}>
                    <td className="font-mono text-sm">{plan.planNumber}</td>
                    <td>
                      {client ? `${client.lastName} ${client.firstName}` : '-'}
                    </td>
                    <td>{planTypeLabels[plan.planType] || plan.planType}</td>
                    <td>{plan.startDate}</td>
                    <td>{plan.endDate || '-'}</td>
                    <td>
                      <span className={statusLabels[plan.status]?.class || 'badge-gray'}>
                        {statusLabels[plan.status]?.label || plan.status}
                      </span>
                    </td>
                    <td>
                      <button className="btn-ghost text-sm py-1 px-2">
                        <HiOutlineEye className="w-4 h-4 mr-1" />
                        詳細
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
